import { createId } from "@/lib/ids";
import type { SceneNode } from "./nodes";
import { createDefaultFrameFlexLayout, createIdentityTransform, type ComponentInstanceNode, type ComponentNode, type ComponentVariantNode, type FrameNode, type PrimitiveNode, type TextNode, type VectorNetworkNode } from "./nodes";
import type { PrimitiveKind } from "./primitives";
import { defaultPrimitiveDefinition } from "./primitives";
import { createDefaultStyle, createSolidPaint, type NodeStyle } from "./styles";
import type { DocumentStyleLibrary } from "./documentStyles";
import { createEmptyStyleLibrary } from "./documentStyles";
import type { ScriptLibrary } from "./documentScripts";
import { createEmptyScriptLibrary } from "./documentScripts";
import { createDefaultRichText, resolvedLineHeight } from "./text";
import type { Guide } from "./guides";
import type { AnimationGraph } from "./animation";
import { createEmptyAnimationGraph } from "./animation";

export interface DocumentMetadata {
  createdAt: string;
  updatedAt: string;
}

export interface Asset {
  id: string;
  type: "raster";
  name: string;
  path: string;
}

export interface Page {
  id: string;
  name: string;
  children: SceneNode[];
  guides?: Guide[];
}

export interface CurvaDocument {
  id: string;
  name: string;
  metadata: DocumentMetadata;
  viewport?: DocumentViewport;
  pages: Page[];
  assets: Asset[];
  styles: DocumentStyleLibrary;
  scripts: ScriptLibrary;
  prototype?: PrototypeGraph;
  animation?: AnimationGraph;
  revision: number;
}

export interface DocumentViewport {
  x: number;
  y: number;
  zoom: number;
}

export interface PrototypeLink {
  id: string;
  sourceNodeId: string;
  targetFrameId: string;
}

export interface PrototypeGraph {
  links: PrototypeLink[];
}

export const DEFAULT_DOCUMENT_VIEWPORT: DocumentViewport = {
  x: 0,
  y: 0,
  zoom: 1,
};

export function createPrimitiveNode(
  kind: PrimitiveKind,
  name: string,
  x: number,
  y: number,
  width: number,
  height: number,
): PrimitiveNode {
  const style: NodeStyle = kind === "line"
    ? {
        fills: [],
        strokes: [{
          paint: createSolidPaint(defaultFillForPrimitive(kind)),
          width: 1,
          align: "inside",
        }],
        stroke: {
          paint: createSolidPaint(defaultFillForPrimitive(kind)),
          width: 1,
          align: "inside",
        },
        opacity: 1,
      }
    : createDefaultStyle(defaultFillForPrimitive(kind));
  return {
    id: createId("node"),
    name,
    type: "primitive",
    visible: true,
    locked: false,
    transform: createIdentityTransform(),
    bounds: { x, y, width, height },
    style,
    primitive: createPrimitiveDefinition(kind),
  };
}

export function createRectangleNode(name: string, x: number, y: number, width: number, height: number): PrimitiveNode {
  return createPrimitiveNode("rectangle", name, x, y, width, height);
}

export function createFrameNode(name: string, x: number, y: number, width: number, height: number): FrameNode {
  return {
    id: createId("frame"),
    name,
    type: "frame",
    visible: true,
    locked: false,
    transform: createIdentityTransform(),
    bounds: { x, y, width, height },
    style: createDefaultStyle("#f8fafc"),
    cornerRadii: [0, 0, 0, 0],
    clipsContent: true,
    autoLayout: "off",
    flexLayout: createDefaultFrameFlexLayout(),
    children: [],
  };
}

export function createComponentVariantNode(name: string, bounds: FrameNode["bounds"], children: SceneNode[]): ComponentVariantNode {
  return {
    id: createId("variant"),
    name,
    type: "componentVariant",
    visible: true,
    locked: false,
    transform: createIdentityTransform(),
    bounds: { ...bounds },
    style: createDefaultStyle("#000000"),
    children,
  };
}

export function createComponentNode(name: string, bounds: FrameNode["bounds"], children: SceneNode[]): ComponentNode {
  const variant = createComponentVariantNode("Default", bounds, children);
  return {
    id: createId("component"),
    name,
    type: "component",
    visible: true,
    locked: false,
    transform: createIdentityTransform(),
    bounds: { ...bounds },
    style: createDefaultStyle("#000000"),
    children: [variant],
  };
}

export function createComponentInstanceNode(component: ComponentNode, variant: ComponentVariantNode, children: SceneNode[]): ComponentInstanceNode {
  return {
    id: createId("instance"),
    name: `${component.name} Instance`,
    type: "instance",
    visible: true,
    locked: false,
    transform: createIdentityTransform(),
    bounds: { ...component.bounds },
    style: createDefaultStyle("#000000"),
    componentId: component.id,
    variantId: variant.id,
    overrides: {},
    children,
  };
}

export function createTextNode(name: string, x: number, y: number, characters = ""): TextNode {
  const text = createDefaultRichText(characters);
  const lineHeight = resolvedLineHeight(text.style);
  return {
    id: createId("text"),
    name,
    type: "text",
    visible: true,
    locked: false,
    transform: createIdentityTransform(),
    bounds: { x, y, width: Math.max(0, characters.length > 0 ? characters.length * text.style.fontSize * 0.62 : 0), height: lineHeight },
    style: createDefaultStyle("#111827"),
    text,
  };
}

export function createSampleVectorNetworkNode(): VectorNetworkNode {
  const v1 = createId("vertex");
  const v2 = createId("vertex");
  const v3 = createId("vertex");
  const v4 = createId("vertex");

  return {
    id: createId("node"),
    name: "Vector Network",
    type: "vectorNetwork",
    visible: true,
    locked: false,
    transform: createIdentityTransform(),
    bounds: { x: 210, y: 300, width: 180, height: 110 },
    style: createDefaultStyle("#60a5fa"),
    network: {
      vertices: [
        { id: v1, x: 210, y: 332 },
        { id: v2, x: 286, y: 300 },
        { id: v3, x: 390, y: 336 },
        { id: v4, x: 260, y: 410 },
      ],
      edges: [
        { id: createId("edge"), from: { vertexId: v1 }, to: { vertexId: v2 }, directed: false },
        { id: createId("edge"), from: { vertexId: v2 }, to: { vertexId: v3 }, directed: false },
        { id: createId("edge"), from: { vertexId: v3 }, to: { vertexId: v4 }, directed: false },
        { id: createId("edge"), from: { vertexId: v4 }, to: { vertexId: v1 }, directed: false },
      ],
      regions: [],
      fillRule: "nonzero",
    },
  };
}

export function createSampleDocument(): CurvaDocument {
  const now = "2026-06-23T00:00:00.000Z";
  const frame: FrameNode = {
    ...createFrameNode("Icon Sheet", 96, 80, 560, 360),
    children: [
      {
        ...createRectangleNode("Primary Rectangle", 168, 148, 176, 112),
        style: createDefaultStyle("#22c55e"),
      },
      {
        ...createPrimitiveNode("oval", "Accent Oval", 392, 166, 128, 128),
        style: createDefaultStyle("#f59e0b"),
      },
      createTextNode("Headline", 170, 104, "Curva Text"),
      createSampleVectorNetworkNode(),
    ],
  };

  return {
    id: createId("doc"),
    name: "Untitled",
    metadata: {
      createdAt: now,
      updatedAt: now,
    },
    viewport: { ...DEFAULT_DOCUMENT_VIEWPORT },
    pages: [
      {
        id: createId("page"),
        name: "Page 1",
        children: [frame],
      },
    ],
    assets: [],
    styles: createEmptyStyleLibrary(),
    scripts: createEmptyScriptLibrary(),
    animation: createEmptyAnimationGraph(),
    revision: 0,
  };
}

export function createBlankDocument(name = "Untitled"): CurvaDocument {
  const now = new Date().toISOString();
  return {
    id: createId("doc"),
    name,
    metadata: {
      createdAt: now,
      updatedAt: now,
    },
    viewport: { ...DEFAULT_DOCUMENT_VIEWPORT },
    pages: [
      {
        id: createId("page"),
        name: "Page 1",
        children: [],
      },
    ],
    assets: [],
    styles: createEmptyStyleLibrary(),
    scripts: createEmptyScriptLibrary(),
    animation: createEmptyAnimationGraph(),
    revision: 0,
  };
}

export function createPage(name: string): Page {
  return {
    id: createId("page"),
    name,
    children: [],
  };
}

export function nextPageName(document: CurvaDocument): string {
  const usedNumbers = new Set(
    document.pages.flatMap(page => {
      const match = /^Page (\d+)$/.exec(page.name);
      return match ? [Number(match[1])] : [];
    }),
  );
  let index = document.pages.length + 1;
  while (usedNumbers.has(index)) {
    index += 1;
  }
  return `Page ${index}`;
}

export function getActivePage(document: CurvaDocument, activePageId?: string): Page | undefined {
  if (activePageId) {
    const page = document.pages.find(candidate => candidate.id === activePageId);
    if (page) {
      return page;
    }
  }
  return document.pages[0];
}

export function duplicatePageName(document: CurvaDocument, name: string): string {
  const base = `${name} copy`;
  if (!document.pages.some(page => page.name === base)) {
    return base;
  }

  let index = 2;
  while (document.pages.some(page => page.name === `${base} ${index}`)) {
    index += 1;
  }
  return `${base} ${index}`;
}

export function fallbackActivePageIdAfterDelete(document: CurvaDocument, deletedPageId: string): string | undefined {
  const deletedIndex = document.pages.findIndex(page => page.id === deletedPageId);
  if (deletedIndex < 0) {
    return document.pages[0]?.id;
  }

  const remaining = document.pages.filter(page => page.id !== deletedPageId);
  if (remaining.length === 0) {
    return undefined;
  }

  const nextIndex = Math.min(deletedIndex, remaining.length - 1);
  return remaining[nextIndex]?.id;
}

export function resolveActivePageId(document: CurvaDocument, activePageId?: string): string | undefined {
  return getActivePage(document, activePageId)?.id;
}

export function getAllSceneNodes(document: CurvaDocument): SceneNode[] {
  return document.pages.flatMap(page => flattenSceneNodes(page.children));
}

export function getActivePageSceneNodes(document: CurvaDocument, activePageId?: string): SceneNode[] {
  const page = getActivePage(document, activePageId);
  return page ? flattenSceneNodes(page.children) : [];
}

export function findSceneNode(document: CurvaDocument, nodeId: string): SceneNode | undefined {
  return getAllSceneNodes(document).find(node => node.id === nodeId);
}

export function findPageIdForSceneNode(document: CurvaDocument, nodeId: string): string | undefined {
  for (const page of document.pages) {
    if (sceneNodeExistsInChildren(page.children, nodeId)) {
      return page.id;
    }
  }
  return undefined;
}

export function getAllComponents(document: CurvaDocument): Extract<SceneNode, { type: "component" }>[] {
  return getAllSceneNodes(document).filter((node): node is Extract<SceneNode, { type: "component" }> => node.type === "component");
}

export function getSceneNodeChildren(node: SceneNode): SceneNode[] | undefined {
  if (node.type === "frame" || node.type === "group" || node.type === "component" || node.type === "componentVariant" || node.type === "instance") {
    return node.children;
  }

  if (node.type === "boolean") {
    return node.sourceNodes;
  }

  return undefined;
}

export function findFirstFrameNode(document: CurvaDocument, activePageId?: string): FrameNode | undefined {
  return getActivePageSceneNodes(document, activePageId).find((node): node is FrameNode => node.type === "frame");
}

function flattenSceneNodes(nodes: SceneNode[]): SceneNode[] {
  return nodes.flatMap(node => {
    const children = getSceneNodeChildren(node);
    return children ? [node, ...flattenSceneNodes(children)] : [node];
  });
}

function sceneNodeExistsInChildren(nodes: SceneNode[], nodeId: string): boolean {
  for (const node of nodes) {
    if (node.id === nodeId) {
      return true;
    }
    const children = getSceneNodeChildren(node);
    if (children && sceneNodeExistsInChildren(children, nodeId)) {
      return true;
    }
  }
  return false;
}

function createPrimitiveDefinition(kind: PrimitiveKind): PrimitiveNode["primitive"] {
  return defaultPrimitiveDefinition(kind);
}

function defaultFillForPrimitive(kind: PrimitiveKind): string {
  switch (kind) {
    case "oval":
      return "#f59e0b";
    case "line":
      return "#e2e8f0";
    case "polygon":
      return "#a78bfa";
    case "star":
      return "#f43f5e";
    case "frame":
      return "#f8fafc";
    case "rectangle":
    default:
      return "#38bdf8";
  }
}
